/**
 * ToolCardShell — shared chrome for every right-rail layer-family Card.
 *
 * Implements the §6 ToolCard frame from the masterplan: title row + icon
 * + scene-item subtitle + state chip + scrollable body + optional footer.
 * Each Card (Basemap, Raster, Terrain, …) composes its sections inside
 * `<ToolCardShell>` so density, focus rings, and 40 px touch targets stay
 * uniform.
 *
 * The shell is intentionally light on logic; lifecycle states (loading,
 * empty, error, permission, stale, conflict, locked) are surfaced via the
 * `<Banner>` and `<Callout>` primitives passed in through `topBanners`.
 */

import { type ReactNode } from 'react';
import { Icon, type IconName, tokens } from '@/design';

export interface ToolCardShellProps {
  /** Material Symbols rounded ligature for the card title row. */
  icon: IconName;
  /** Visible card title (e.g. "Basemap & imagery"). */
  title: string;
  /** Optional scene-item / layer subtitle in mono. */
  subtitle?: string | null;
  /** Banners (loading / error / permission / stale) rendered above body. */
  topBanners?: ReactNode;
  /** Right-aligned single quick-action; observes single-CTA per row rule. */
  primaryAction?: ReactNode;
  /** Body content — sections of controls. */
  children: ReactNode;
  /** Optional footer (provenance / attribution / debug). */
  footer?: ReactNode;
  /** Surface elevation; default 2 (right-rail panel level). */
  level?: 0 | 1 | 2 | 3;
  'data-testid'?: string;
}

export function ToolCardShell({
  icon,
  title,
  subtitle,
  topBanners,
  primaryAction,
  children,
  footer,
  level = 2,
  'data-testid': testId,
}: ToolCardShellProps) {
  return (
    <section
      role="region"
      aria-label={title}
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[3],
        padding: tokens.space[3],
        background: tokens.color.surface[level],
        color: tokens.color.ink.primary,
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.lg,
        boxShadow: tokens.elevation[1],
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
          minHeight: tokens.density.touchMin,
        }}
      >
        <Icon name={icon} size={24} decorative />
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.semibold,
              lineHeight: tokens.font.lineHeight.tight,
            }}
          >
            {title}
          </h2>
          {subtitle ? (
            <div
              style={{
                fontFamily: tokens.font.family.mono,
                fontSize: tokens.font.role.engMeta.size,
                lineHeight: tokens.font.role.engMeta.lineHeight,
                color: tokens.color.ink.secondary,
                marginTop: tokens.space[1],
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
        {primaryAction}
      </header>
      {topBanners ? <div>{topBanners}</div> : null}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[3],
        }}
      >
        {children}
      </div>
      {footer ? (
        <footer
          style={{
            paddingTop: tokens.space[2],
            borderTop: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          {footer}
        </footer>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Re-usable row primitives consumed by Cards 1–3.
// ---------------------------------------------------------------------------

export interface ToolCardSectionProps {
  title?: string;
  description?: string;
  children: ReactNode;
  'data-testid'?: string;
}

export function ToolCardSection({
  title,
  description,
  children,
  'data-testid': testId,
}: ToolCardSectionProps) {
  return (
    <section
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      {title ? (
        <h3
          style={{
            margin: 0,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            fontWeight: tokens.font.weight.medium,
            lineHeight: tokens.font.role.label.lineHeight,
            color: tokens.color.ink.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {title}
        </h3>
      ) : null}
      {description ? (
        <div
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          {description}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export interface ToolCardSliderRowProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  /** When provided, the readout is rendered in mono at the row trailing edge. */
  readout?: string;
  /** Optional leading icon for the row (e.g. `flare` for brightness). */
  icon?: IconName;
  onChange: (next: number) => void;
  /** Greys out the slider and blocks input. Mirrors `ToolCardToggleRow`. */
  disabled?: boolean;
  'aria-describedby'?: string;
  'data-testid'?: string;
}

export function ToolCardSliderRow({
  label,
  min,
  max,
  step = 1,
  value,
  readout,
  icon,
  onChange,
  disabled,
  'aria-describedby': describedBy,
  'data-testid': testId,
}: ToolCardSliderRowProps) {
  return (
    <label
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[1],
        minHeight: tokens.density.touchMin,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: tokens.space[2],
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.primary,
          }}
        >
          {icon ? <Icon name={icon} size={20} decorative /> : null}
          {label}
        </span>
        {readout ? (
          <span
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
              color: tokens.color.ink.secondary,
            }}
            aria-live="polite"
          >
            {readout}
          </span>
        ) : null}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={readout ?? String(value)}
        onChange={(e) => onChange(Number(e.currentTarget.value))}
        style={{
          width: '100%',
          accentColor: tokens.color.accent.primary,
          minHeight: tokens.density.touchMin,
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
    </label>
  );
}

export interface ToolCardToggleRowProps {
  label: string;
  description?: string;
  icon?: IconName;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

export function ToolCardToggleRow({
  label,
  description,
  icon,
  checked,
  onChange,
  disabled,
  'data-testid': testId,
}: ToolCardToggleRowProps) {
  return (
    <label
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        minHeight: tokens.density.touchMin,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {icon ? <Icon name={icon} size={20} decorative /> : null}
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.primary,
          }}
        >
          {label}
        </span>
        {description ? (
          <span
            style={{
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            {description}
          </span>
        ) : null}
      </span>
      <input
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
        style={{
          width: tokens.size.switchMinWidth,
          height: tokens.size.switchMinHeight,
          accentColor: tokens.color.accent.primary,
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
    </label>
  );
}
